import { Button, getChannel, Input, usePhoneNumberMask, useTheme } from '@ytl/common-web';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useApp } from '../../../../contexts/appContext';
import { useLanguage } from '../../../../contexts/languageContext';
import useRecaptcha from '../../../../modules/useRecaptcha';
import {
  getAnalytics,
  InitParams,
  StepperControls,
  VerificationPath,
  VerificationPhase,
} from '../../../../modules/verificationComponent/VerificationComponent';
import { WidgetEvent } from '../../../@types/widgetEvents';
import { verificatorResource } from '../../../resource/verificator.resource';
import { emitProcessPhase, emitWidgetEvent } from '../../../utils/emitData';
import { formatMsisdn } from '../../../utils/phoneNumberFormatter';
import { RecaptchaPortal } from '../../RecaptchaPortal';

type AddDifferentPhoneNumberProps = {
  stepper: StepperControls;
  initParams?: InitParams | null;
  setPhoneNumber: (phoneNumber: string) => void;
  sessionId: string;
  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;
  isFinished: boolean;
  corrId?: string;
  enableAnalytics?: boolean;
};

export const AddDifferentPhoneNumber = ({
  stepper,
  initParams,
  setPhoneNumber,
  sessionId,
  isLoading,
  setIsLoading,
  isFinished,
  corrId,
  enableAnalytics = false,
}: AddDifferentPhoneNumberProps) => {
  const { translation, language } = useLanguage();
  const { isWebView } = useTheme();

  const borgAnalytics = getAnalytics(enableAnalytics);
  const [localPhoneNumber, setLocalPhoneNumber] = useState('');
  const [validationErrorList, setValidationErrorList] = useState<Array<string>>([]);
  const phoneNumberInput = usePhoneNumberMask();
  const waitingForCaptchaRef = useRef(false);
  const {
    recaptchaRef,
    recaptchaForceRender,
    recaptchaLang,
    handleRecaptcha,
    captchaToken,
    recaptchaSiteKey,
    recaptchaInvisible,
    executeInvisibleCaptcha,
    needsCaptcha,
  } = useRecaptcha();
  const { isWidgetMode } = useApp();
  const showCaptcha = isWidgetMode ? initParams?.captchaNeeded : needsCaptcha;

  useEffect(() => {
    emitProcessPhase('AddDifferentPhoneNumber');
    emitWidgetEvent(
      {
        eventName: WidgetEvent.AddDifferentPhoneNumberOpened,
        eventValue: {},
        isSuccess: true,
      },
      { sessionId },
    );
  }, [sessionId]);

  const handleContinue = useCallback(async () => {
    if (formatMsisdn(localPhoneNumber).length < 9) {
      setValidationErrorList([translation.yeppRegistration.webcomponent.invalidPhoneNumber]);
      return;
    }
    // If invisible captcha is enabled and no token yet, trigger captcha execution
    if (showCaptcha && recaptchaInvisible && !captchaToken) {
      waitingForCaptchaRef.current = true;
      await executeInvisibleCaptcha();
      return; // Wait for captcha token via effect
    }

    if (showCaptcha && !captchaToken) {
      setValidationErrorList([translation.yeppRegistration.webcomponent.captchaRequired]);
      return;
    }

    waitingForCaptchaRef.current = false;
    setPhoneNumber(formatMsisdn(localPhoneNumber));
    setIsLoading(true);

    try {
      //TODO decode jwt minden ellenőrzés erlőtt
      const verifyResponsePhone = await verificatorResource.validateMsisdn(
        {
          sessionId: sessionId,
          localPhoneNumber: `${formatMsisdn(localPhoneNumber, true)}`,
        },
        corrId,
      );

      // Check for HTTP error status codes (4xx and 5xx)
      if (verifyResponsePhone.status >= 400) {
        borgAnalytics.sendRegistrationEvent({
          channel: getChannel(isWebView),
          virtualName: 'validateMsisdn',
          action: 'TechnicalError',
          result: 'false',
          reason: `HTTP Error ${verifyResponsePhone.status}`,
        });
        emitWidgetEvent(
          {
            eventName: WidgetEvent.TechnicalError,
            eventValue: {
              process: 'validateMsisdn',
              details: `HTTP Error ${verifyResponsePhone.status}: ${verifyResponsePhone.statusText}`,
            },
            isSuccess: false,
          },
          { sessionId },
        );
        setIsLoading(false);
        return;
      }

      // Check for application-level errors in response data
      if (verifyResponsePhone.data.status === 'error') {
        borgAnalytics.sendRegistrationEvent({
          channel: getChannel(isWebView),
          virtualName: 'validateMsisdn',
          action: 'TechnicalError',
          result: 'false',
          reason: `${verifyResponsePhone.data}`,
        });
        emitWidgetEvent(
          {
            eventName: WidgetEvent.TechnicalError,
            eventValue: {
              process: 'validateMsisdn',
              details: verifyResponsePhone.data,
            },
            isSuccess: false,
          },
          { sessionId },
        );
        setIsLoading(false);
        return;
      }

      if (verifyResponsePhone.data.status === 'verification_needed') {
        const verifyResponseSendCode = await verificatorResource.sendMsisdnVerificationCode(
          {
            sessionId: sessionId,
            lang: initParams?.language || language,
            clientId: initParams?.clientID,
          },
          corrId,
        );

        // Check for HTTP error status codes (4xx and 5xx)
        if (verifyResponseSendCode.status >= 400) {
          borgAnalytics.sendRegistrationEvent({
            channel: getChannel(isWebView),
            virtualName: 'sendMsisdnVerificationCode',
            action: 'TechnicalError',
            result: 'false',
            reason: `HTTP Error ${verifyResponseSendCode.status}`,
          });
          emitWidgetEvent(
            {
              eventName: WidgetEvent.TechnicalError,
              eventValue: {
                process: 'sendMsisdnVerificationCode',
                details: `HTTP Error ${verifyResponseSendCode.status}: ${verifyResponseSendCode.statusText}`,
              },
              isSuccess: false,
            },
            { sessionId },
          );
          setIsLoading(false);
          return;
        }

        // Check for application-level errors in response data
        if (verifyResponseSendCode.data.status === 'error') {
          borgAnalytics.sendRegistrationEvent({
            channel: getChannel(isWebView),
            virtualName: 'sendMsisdnVerificationCode',
            action: 'TechnicalError',
            result: 'false',
            reason: `${verifyResponseSendCode.data}`,
          });
          emitWidgetEvent(
            {
              eventName: WidgetEvent.TechnicalError,
              eventValue: {
                process: 'sendMsisdnVerificationCode',
                details: verifyResponseSendCode.data,
              },
              isSuccess: false,
            },
            { sessionId },
          );
          setIsLoading(false);
          return;
        }

        if (verifyResponseSendCode.data.status === 'banned' || verifyResponseSendCode.data.status === 'limit_reached') {
          borgAnalytics.sendRegistrationEvent({
            channel: getChannel(isWebView),
            virtualName: 'Temporary termination ctn',
            action: 'ProcessFinished',
            result: 'false',
            reason: 'banned',
          });
          emitWidgetEvent(
            {
              eventName: WidgetEvent.SMSVerificationCodeSent,
              eventValue: { reason: verifyResponseSendCode.data.status },
              isSuccess: false,
            },
            { sessionId },
          );
          setIsLoading(false);
          stepper.goToPhase(VerificationPhase.Banned);
          return;
        }
        borgAnalytics.sendRegistrationEvent({
          channel: getChannel(isWebView),
          virtualName: 'Ctn authentication sms code',
          action: 'SMSVerification',
          result: 'true',
        });
        emitWidgetEvent(
          {
            eventName: WidgetEvent.SMSVerification,
            eventValue: { msisdn: formatMsisdn(localPhoneNumber) },
            isSuccess: true,
          },
          { sessionId },
        );
        borgAnalytics.sendRegistrationEvent({
          channel: getChannel(isWebView),
          virtualName: 'Ctn authentication sms code',
          action: 'SMSVerificationCodeSent',
          result: 'true',
        });
        emitWidgetEvent(
          {
            eventName: WidgetEvent.SMSVerificationCodeSent,
            eventValue: {},
            isSuccess: true,
          },
          { sessionId },
        );
        stepper.addPath(VerificationPath.PhoneConfirmRequired);
        stepper.goToNext();
        return;
      }
      if (verifyResponsePhone.data.status === 'invalid') {
        borgAnalytics.sendRegistrationEvent({
          channel: getChannel(isWebView),
          virtualName: 'Ctn authentication sms code',
          action: 'SMSVerification',
          result: 'false',
          reason: 'invalid',
        });
        emitWidgetEvent(
          {
            eventName: WidgetEvent.SMSVerification,
            eventValue: { msisdn: formatMsisdn(localPhoneNumber), reason: verifyResponsePhone.data.status },
            isSuccess: false,
          },
          { sessionId },
        );
        setValidationErrorList([translation.yeppRegistration.webcomponent.invalidPhoneNumber]);
        setIsLoading(false);
        return;
      }
      if (verifyResponsePhone.data.status === 'banned') {
        borgAnalytics.sendRegistrationEvent({
          channel: getChannel(isWebView),
          virtualName: 'Temporary termination ctn',
          action: 'ProcessFinished',
          result: 'false',
          reason: 'banned',
        });
        setIsLoading(false);
        stepper.goToPhase(VerificationPhase.Banned);
        return;
      }
      if (verifyResponsePhone.data.status === 'valid') {
        emitWidgetEvent(
          {
            eventName: WidgetEvent.SMSVerification,
            eventValue: { msisdn: formatMsisdn(localPhoneNumber) },
            isSuccess: true,
          },
          { sessionId },
        );
        borgAnalytics.sendRegistrationEvent({
          channel: getChannel(isWebView),
          virtualName: 'Ctn authentication start',
          action: 'SMSVerificationBypass',
          result: 'true',
        });
        emitWidgetEvent(
          {
            eventName: WidgetEvent.SMSVerificationBypass,
            eventValue: {},
            isSuccess: true,
          },
          { sessionId },
        );
        stepper.goToNext();
        return;
      }
      setIsLoading(false);
      stepper.goToPhase(VerificationPhase.Error);
    } catch (err) {
      console.error('Failed in phone verification:', err);
      borgAnalytics.sendRegistrationEvent({
        channel: getChannel(isWebView),
        virtualName: 'AddDifferentPhoneNumber',
        action: 'TechnicalError',
        result: 'false',
        reason: `${err}`,
      });
      emitWidgetEvent(
        {
          eventName: WidgetEvent.TechnicalError,
          eventValue: {
            process: 'AddDifferentPhoneNumber',
            details: err,
          },
          isSuccess: false,
        },
        { sessionId },
      );
      setIsLoading(false);
    }
  }, [
    recaptchaInvisible,
    initParams,
    captchaToken,
    executeInvisibleCaptcha,
    translation,
    setPhoneNumber,
    localPhoneNumber,
    sessionId,
    stepper,
    language,
    setIsLoading,
    corrId,
    showCaptcha,
    borgAnalytics,
    isWebView,
  ]);

  // Auto-retry when captcha token arrives after invisible captcha execution
  useEffect(() => {
    if (waitingForCaptchaRef.current && captchaToken) {
      waitingForCaptchaRef.current = false;
      handleContinue();
    }
  }, [captchaToken, handleContinue]);

  const handleBack = useCallback(() => {
    const canReturnToSelectPhoneNumber = stepper.hasPhase(VerificationPhase.SelectPhoneNumber);

    emitWidgetEvent(
      {
        eventName: WidgetEvent.SMSVerificationBack,
        eventValue: {},
        isSuccess: false,
      },
      { sessionId },
    );
    stepper.removePath(VerificationPath.CustomPhoneNumber);
    stepper.removePath(VerificationPath.PhoneConfirmRequired);
    if (canReturnToSelectPhoneNumber) {
      stepper.goToPhase(VerificationPhase.SelectPhoneNumber);
      return;
    }
    stepper.goToPrevious();
  }, [stepper, sessionId]);

  return (
    <div className={`verification-step${isWebView ? ' verification-step--webview' : ''}`}>
      <div className="register-page__container__box__inputs">
        <Input
          label={translation.yeppRegistration.webcomponent.phoneNumber}
          name={'phonenumber'}
          type={'text'}
          inputMode={'tel'}
          value={localPhoneNumber}
          onChange={e => {
            setLocalPhoneNumber(e.target.value);
            setValidationErrorList([]);
          }}
          disabled={isLoading || isFinished}
          maxLength={11}
          ref={phoneNumberInput}
          hasError={validationErrorList.length > 0}
          errorList={validationErrorList}
          required={true}
        />
      </div>

      {showCaptcha && (
        <RecaptchaPortal
          recaptchaRef={recaptchaRef}
          sitekey={recaptchaSiteKey}
          hl={recaptchaLang}
          onChange={handleRecaptcha}
          className={'verification-step__recaptcha'}
          asyncScriptOnLoad={recaptchaForceRender}
          size={recaptchaInvisible ? 'invisible' : undefined}
          themeOverride={initParams?.theme}
          languageOverride={initParams?.language}
        />
      )}

      <div className={`verification-step__actions${isWebView ? ' verification-step__actions--webview' : ''}`}>
        <Button
          variant="contained"
          onClick={handleContinue}
          disabled={isLoading || isFinished || formatMsisdn(localPhoneNumber).length < 9}
        >
          {translation.yeppRegistration.webcomponent.continue}
        </Button>
        {stepper.hasPhase(VerificationPhase.SelectPhoneNumber) && (
          <Button
            variant={'text-undecorated'}
            onClick={handleBack}
            disabled={isLoading || isFinished}
          >
            {translation.yeppRegistration.webcomponent.back}
          </Button>
        )}
      </div>
    </div>
  );
};
